import type { JsonValue } from "@postdaily/api/lib/json";

import { readConsent } from "./cookie-consent";
import { withPostHog } from "./posthog";

/**
 * The product events we capture from the browser. Keeping them in one union
 * keeps call sites consistent and typo-free — add a new event here before
 * calling `track` with it, and describe it in docs/analytics/tracking-plan.md
 * (the source of truth for names, properties and meaning).
 *
 * These are person-level and consent-gated. Outcomes that must be counted for
 * everyone — signups, publishing, billing — are server events instead
 * (`packages/api/src/services/analytics/posthog.ts`).
 */
export type AnalyticsEvent =
  | "ai_assistant_action_confirmed"
  | "ai_assistant_action_declined"
  | "ai_assistant_alt_text_applied"
  | "ai_assistant_apply"
  | "ai_assistant_brand_voice_saved"
  | "ai_assistant_conversation_created"
  | "ai_assistant_credit_limit_hit"
  | "ai_assistant_idea_drafted"
  | "ai_assistant_message_sent"
  | "ai_assistant_opened"
  | "ai_assistant_translate"
  | "ai_assistant_undo"
  | "ai_assistant_variant_used"
  | "analytics_exported"
  | "analytics_tab_viewed"
  | "approval_requested"
  | "bulk_import_completed"
  | "channel_connect_failed"
  | "channel_connect_started"
  | "channel_connected"
  | "checkout_started"
  | "composer_opened"
  | "dashboard_suggestion_clicked"
  | "feedback_submitted"
  | "image_edited"
  | "invite_accepted"
  | "media_collection_created"
  | "media_uploaded"
  | "member_invited"
  | "notification_clicked"
  | "onboarding_completed"
  | "onboarding_skipped"
  | "onboarding_step_viewed"
  // An existing subscriber's plan change, once the server has applied or
  // scheduled it.
  | "plan_change_confirmed"
  | "plan_selected"
  | "post_deleted"
  // "Save draft" succeeded. Named `post_created` before 2026-09-26.
  | "post_draft_saved"
  | "post_duplicated"
  | "post_published_now"
  | "post_queued"
  | "post_rescheduled"
  | "post_retried"
  | "post_scheduled"
  | "posting_schedule_saved"
  | "publish_layout_changed"
  | "queue_action"
  | "review_link_action"
  | "subscription_activated"
  | "template_saved"
  | "template_used"
  // One event for every free public tool, carrying `tool` and `action`
  // properties. Per-tool event names would multiply the union with each new
  // page and make cross-tool comparison a manual join.
  | "tool_used"
  | "upgrade_prompt_viewed"
  | "user_signed_up"
  | "workspace_switched";

/**
 * Event properties, as they will be sent.
 *
 * PostHog serializes the payload to JSON, so this is the widest a value can
 * usefully be — anything outside it is silently reshaped in transit rather than
 * arriving as the caller wrote it. `undefined` is allowed because an optional
 * field read off a DTO is the common case; it is dropped on encode.
 */
export type AnalyticsProperties = {
  [key: string]: JsonValue | undefined;
};

/**
 * Capture a product event. No-op when PostHog isn't configured (local/dev), so
 * call sites never need their own guard.
 */
export function track(event: AnalyticsEvent, properties?: AnalyticsProperties) {
  withPostHog((client) => {
    client.capture(event, properties);
  });
}

/**
 * Capture an event at most once per tab for `onceKey` — for pages a refresh
 * would otherwise count again (the checkout success page). The marker is
 * analytics storage, so like the event it is only written with consent, and
 * its `ph_` prefix means withdrawing consent erases it.
 */
export function trackOncePerTab(
  onceKey: string,
  event: AnalyticsEvent,
  properties?: AnalyticsProperties,
) {
  if (typeof window === "undefined" || !readConsent().analytics) {
    return;
  }

  const storageKey = `ph_postdaily_once_${onceKey}`;

  try {
    if (window.sessionStorage.getItem(storageKey)) {
      return;
    }
    window.sessionStorage.setItem(storageKey, "1");
  } catch {
    // Storage unavailable: count it rather than lose it.
  }

  track(event, properties);
}

const SIGNUP_TRACKED_KEY = "postdaily.signup_tracked";

/**
 * Capture `user_signed_up` at most once per browser. The email form calls this
 * on success; new Google users (who land on `/onboarding`) call it on wizard
 * mount — the flag stops the two paths from double-counting the same signup.
 */
export function trackSignupOnce(method: "email" | "google") {
  if (typeof window === "undefined") {
    return;
  }
  // The flag exists only to deduplicate an analytics event, which makes it
  // analytics storage — writing it without consent would be the same breach as
  // the event itself, just quieter.
  if (!readConsent().analytics) {
    return;
  }
  if (window.localStorage.getItem(SIGNUP_TRACKED_KEY)) {
    return;
  }
  window.localStorage.setItem(SIGNUP_TRACKED_KEY, "1");
  track("user_signed_up", { method });
}
